import { ReactElement, useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { SimpleContent, TabsActions } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { LoyaltyHeader } from 'features/Loyalty/components/Header/'
import { LoyaltyTabs } from 'features/Loyalty/components/LoyaltyTabs'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'
import { LoyaltyCampaignsList } from 'features/Loyalty/views/LoyaltyCampaigns/List'
import { LoyaltyPagination } from 'features/Loyalty/views/LoyaltyCampaigns/Pagination'
import { GTQuery } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'

const LIMIT = 30
const messages = defineMessages({
  helmet: {
    id: 'loyalty.campaigns.helmet',
    defaultMessage: 'Loyalty Campaigns | Nexbus Admin'
  }
})

const GET_LOYALTY_CAMPAIGNS = gql`
  query GetLoyaltyCampaigns($limit: Int!, $page: Int!) {
    loyaltyCampaigns(pagination: { size: $limit, page: $page }, input: {}) {
      total
      items {
        company {
          id
          name
        }
        companyLines {
          id
          name
        }
        description
        endAt
        id
        name
        rules {
          description
          details
          howToGet
          id
          name
          validAt
        }
        startAt
        status
      }
    }
  }
`
const LoyaltyCampaignsView = (): ReactElement => {
  let content
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()

  const hasPermission = canRead(FEATURES.LOYALTY_CAMPAIGN, login.accessToken.policies)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let { page = 1 } = getQueries(location.search)
  page = parseInt(String(page))
  const { loading, error, data, refetch, networkStatus } = useQuery<Pick<GTQuery, 'loyaltyCampaigns'>>(
    GET_LOYALTY_CAMPAIGNS,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        limit: LIMIT,
        page: page - 1,
        companyId: login.accessToken.companyId
      },
      skip: !hasPermission
    }
  )

  if (!hasPermission) {
    return <ForbiddenView feature={FEATURES.LOYALTY_CAMPAIGN} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.campaigns.error.description"
            defaultMessage="There was an error loading your campaigns"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="spanish.campaigns.error.action" defaultMessage="Reload campaigns" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.loyaltyCampaigns.items) && page === 1) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.campaigns.empty.description" defaultMessage="You don't have campaigns yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/loyalty/campaign/new" kind="primary">
            <FormattedMessage id="spanish.campaigns.empty.action" defaultMessage="Add new campaign" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (!data?.loyaltyCampaigns || isEmpty(data?.loyaltyCampaigns.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.campaigns.empty.filters.description" defaultMessage="No campaigns were found" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/loyalty/campaigns" kind="primary">
            <FormattedMessage id="spanish.campaigns.empty.filters.action" defaultMessage="Erase filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <LoyaltyCampaignsList campaigns={data.loyaltyCampaigns.items} />
        <LoyaltyPagination
          page={page}
          limit={LIMIT}
          totalItems={data.loyaltyCampaigns.total}
          currentItemsCount={data.loyaltyCampaigns.items.length}
        />
      </>
    )
  }

  return (
    <>
      <LoyaltyHeader />
      <LoyaltyTabs />
      <SimpleContent>
        {canWrite(FEATURES.LOYALTY_CAMPAIGN, login.accessToken.policies) && (
          <TabsActions>
            <Button as={NavLink} to="/loyalty/campaigns/new" kind="primary">
              <FormattedMessage id="spanish.campaigns.header.new" defaultMessage="+ Add new campaign" />
            </Button>
          </TabsActions>
        )}
        <Card.Container>
          <Card.Content noHeader noFooter>
            {content}
          </Card.Content>
        </Card.Container>
      </SimpleContent>
    </>
  )
}

export default withLastUrl(LoyaltyCampaignsView)
